"use client";
import { useState, useEffect, forwardRef, useImperativeHandle, useCallback } from "react";
import { useForm, useWatch } from "react-hook-form";
import { FaCheck, FaCloudUploadAlt } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import { GrView } from "react-icons/gr";
import { IoMdInformationCircleOutline } from "react-icons/io";
import PropTypes from 'prop-types';
import listaArchivosRequeridos from "../data/archivosRequeridos.json";

const Step9 = forwardRef(function Step9(props, ref) {
  const { dataForm, show } = props;

  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors }
  } = useForm({
    defaultValues: {
      archivos: {
        escrituraAdquisicion: null,
        minuta: null,
        certificadoPropiedad: null
      }
    }
  });

  const watchArchivos = useWatch({
    control,
    name: "archivos"
  });
  const files = watchArchivos || {};

  const [previews, setPreviews] = useState({});

  const isFileLike = (value) => {
    return typeof File !== "undefined" && value instanceof File;
  };

  const createPreviewUrl = useCallback((file) => {
    if (!isFileLike(file)) return null;
    return URL.createObjectURL(file);
  }, []);

  useEffect(() => {
    if (!dataForm) return;

    const objectUrls = [];

    Object.entries(dataForm).forEach(([name, file]) => {
      if (!file) return;

      setValue(`archivos.${name}`, file);

      const url = createPreviewUrl(file);
      if (!url) return;

      objectUrls.push(url);
      setPreviews(prev => ({ ...prev, [name]: url }));
    });

    return () => {
      objectUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [dataForm, setValue, createPreviewUrl]);

  const handleUpload = (e, name) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setValue(`archivos.${name}`, file, {
      shouldValidate: true,
      shouldDirty: true,
    });

    const url = createPreviewUrl(file);
    if (!url) return;

    setPreviews((prev) => {
      if (prev[name]) {
        URL.revokeObjectURL(prev[name]);
      }

      return { ...prev, [name]: url };
    });
  };

  const handleDelete = (name) => {
    show({
      title: "Confirmar eliminación",
      message: "¿Estás seguro de eliminar este archivo cargado?",
      type: "question",
      onConfirmation: () => {
        setValue(`archivos.${name}`, null);
        if (previews[name]) {
          URL.revokeObjectURL(previews[name]);
          setPreviews((prev) => {
            const copy = { ...prev };
            delete copy[name];
            return copy;
          });
        }
      },
    });
  };

  useImperativeHandle(ref, () => ({
    ejecutarSubmit: () => {
      return new Promise((resolve, reject) => {
        handleSubmit(
          (data) => resolve(data),
          (formErrors) => reject(formErrors),
        )();
      });
    },
  }));

  return (
    <form className='flex flex-col gap-5.5'>
      {/* Subida de Archivos */}
      <div>
        <h3 className="card-title mb-4">Carga de archivos</h3>

        <table className="table table-zebra w-full border">
          <thead>
            <tr className="bg-gray-200">
              <th className="px-2 py-1 text-left">#</th>
              <th className="px-2 py-1 text-left">Requisito</th>
              <th className="px-2 py-1 text-left">Tipo</th>
              <th className="px-2 py-1 text-center">Acciones</th>
              <th className="px-2 py-1 text-center">Estado</th>
            </tr>
          </thead>
          <tbody>
            {listaArchivosRequeridos
              .map((req, idx) => {
                const isUploaded = Boolean(files[req.name]);
                return (
                  <tr key={req.name} className="border-b">
                    <td className="px-2 py-2">{idx + 1}</td>
                    <td className="px-2 py-2">{req.label}</td>
                    <td className="px-2 py-2">
                      {req.accept.split(",")[0].substring(1)}
                    </td>

                    <td className="px-2 py-2">
                      {isUploaded ? (
                        <div className="flex justify-center gap-2">
                          {/* Botón Ver/Descargar */}
                          <a
                            href={previews[req.name]}
                            // target="_blank"
                            download
                            rel="noopener noreferrer"
                            className="btn btn-info btn-sm flex flex-1 items-center justify-center gap-1"
                          >
                            <GrView size={16} />
                          </a>
                          {/* Botón Borrar */}
                          <button
                            type="button"
                            onClick={() => handleDelete(req.name)}
                            className="btn btn-error btn-sm flex flex-1 items-center justify-center gap-1"
                          >
                            <MdDelete size={16} />
                          </button>
                        </div>
                      ) : (
                        <div>
                          <label className="btn btn-primary btn-sm flex cursor-pointer items-center gap-1">
                            <FaCloudUploadAlt size={16} />
                            <input
                              type="file"
                              accept={req.accept}
                              className="hidden"
                              {...register(`archivos.${req.name}`, {
                                required: req.required ? `Suba el archivo ${req.label}` : false,
                              })}
                              onChange={(e) => handleUpload(e, req.name)}
                            />
                          </label>
                          {errors.archivos?.[req.name] && (
                            <p className="flex items-center text-red-500">
                              {" "}
                              <IoMdInformationCircleOutline />{" "}
                              {errors.archivos?.[req.name].message}
                            </p>
                          )}
                        </div>
                      )}
                    </td>

                    <td className="px-2 py-2 text-center">
                      {isUploaded && (
                        <FaCheck
                          className="inline-block text-green-600"
                          size={20}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </form>
  );
});

export default Step9;
Step9.displayName = 'Step9';

Step9.propTypes = {
  setLoading: PropTypes.func.isRequired,
  dataForm: PropTypes.object,
  show: PropTypes.func.isRequired,
};
